import { Component, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticEvents, FileUploadDirectories } from '@app/enums';
import { NavigationGuard } from '@app/guards';
import { CompanyDocumentUploadable } from '@app/modules/documents-v2/classes/company-document-uploadable';
import { DocumentFoldersService } from '@app/modules/documents-v2/services/document-folders.service';
import { DocumentNameAvailableService } from '@app/modules/documents-v2/services/document-name-available.service';
import { AnalyticService, FileHelperService, NotifyService } from '@app/services';
import { Translatable, TranslatableKey } from '@app/types/translatable.type';
import { DriveTypeDisplayOptions } from '@app/types/translatables/drive-name-display.options';
import { BulkUploadView } from '@common/views/bulk-upload/bulk-upload-view';
import { DocumentAllowedFileTypes, DocumentType, DriveTypeEnum } from '@documents-v2/enums';
import { CompanyDocument } from '@models/documents/company-document.model';
import { Drive } from '@models/documents/directory/drive.model';
import { Node } from '@models/documents/directory/node.model';
import { DocumentTag } from '@models/documents/document-tag.model';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './company-documents-bulk-upload.template.html',
    styleUrls: ['../../../../../styles/shared-views/documents-bulk-upload.scss'],
})
export class CompanyDocumentsBulkUploadView extends BulkUploadView {
    isLoading = true;
    uploadLimit = 25;
    uploadDescriptor = 'documents';
    uploadables: CompanyDocumentUploadable[] = [];
    allowedFileTypes = DocumentAllowedFileTypes.acknowledgement
        .split(',')
        .map((ext) => `.${ext}`)
        .join(',');

    /**
     * A map of names that we know are either "available" or "not available" from the back end
     * We use this map so we don't have to re-query the data if the user asks more than once
     */
    availableNames = new Map<string, boolean>();
    folderId: number = 0;
    parentDrive?: Drive;
    backRoute = '/documents-v2/center';
    queryParamSubscription!: Subscription;

    /**
     * Tags used in the tags selection component
     * Shows existing company document tags
     *
     * Also stops us from trying to persist a tag if it already exists
     */
    companyDocumentTags: DocumentTag[] = [];

    headerTitle: TranslatableKey = '';
    headerSubTitle: Translatable = '';

    driveTypeDisplay = DriveTypeDisplayOptions;
    driveTypeEnum = DriveTypeEnum;
    isUploadingViewOnly: boolean = false;

    constructor(
        fileHelperService: FileHelperService,
        notifyService: NotifyService,
        private documentNameAvailableService: DocumentNameAvailableService,
        private documentFoldersService: DocumentFoldersService,
        private analyticService: AnalyticService,
        private route: ActivatedRoute,
        private readonly translateService: TranslateService
    ) {
        super(fileHelperService, notifyService);
    }

    uploadableCreator = (file: File): CompanyDocumentUploadable => new CompanyDocumentUploadable(file);

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (this.status === 'uploading') {
            $event.returnValue = NavigationGuard.warningMessage;
        }
    }

    ngOnDestroy(): void {
        this.queryParamSubscription.unsubscribe();
    }

    protected async onInit(): Promise<void> {
        this.loadTags();
        this.queryParamSubscription = this.route.queryParams.subscribe(async (params) => {
            this.folderId = params?.folderId;
            if (this.folderId) {
                this.backRoute = '/documents-v2/center/folders/' + this.folderId.toString();

                this.parentDrive = await this.documentFoldersService.getDriveBasedOnNodeId(this.folderId);
                const destinationFolder = await Node.find(this.folderId);

                this.headerTitle =
                    this.parentDrive.type === DriveTypeEnum.public
                        ? 'document.uploadViewOnlyDocuments'
                        : 'document.uploadAcknowledgementDocuments';

                this.headerSubTitle = {
                    key: 'document.toTheDestinationFolder',
                    params: { destinationFolder: this.destinationFolderName(destinationFolder) },
                };
            } else {
                this.headerTitle = 'document.uploadViewOnlyDocuments';
            }
            this.isUploadingViewOnly = !!(this.parentDrive && this.parentDrive.type === DriveTypeEnum.public);
            this.isLoading = false;
        });
    }

    protected destinationFolderName(destinationFolder: Node): string {
        if (destinationFolder?.isDrive()) {
            return this.parentDrive?.type === this.driveTypeEnum.private
                ? this.translateService.instant(this.driveTypeDisplay['private'])
                : this.translateService.instant(this.driveTypeDisplay['public']);
        }
        return destinationFolder.name;
    }

    protected async onReset(): Promise<void> {
        this.loadTags();
        this.availableNames.clear();
        this.analyticService.trackEvent(AnalyticEvents.BulkUploaderViewOnlyDocumentReset);
    }

    protected onAdded(uploadables: CompanyDocumentUploadable[]): void {
        uploadables.forEach((uploadable) => {
            if (uploadable.hasInvalidFile()) {
                this.analyticService.trackEvent(AnalyticEvents.BulkUploaderAddViewOnlyDocumentError, {
                    invalidFileSize: uploadable.isTooLarge,
                    invalidFileType: uploadable.hasWrongFileType,
                });
            }
        });
    }

    protected onRemove(uploadable: CompanyDocumentUploadable): void {
        this.analyticService.trackEvent(AnalyticEvents.BulkUploaderViewOnlyDocumentRemoved, {
            fileHadError: uploadable.hasInvalidFile(),
        });
    }

    protected async onValidate(): Promise<void> {
        this.validateAgainstLocalNames();
        await this.validateAgainstPersistedNames();
    }

    protected async onUpload(): Promise<void> {
        await this.persistNotPersistedTags();

        const uploadPromises = this.uploadables.map(async (uploadable) => {
            try {
                const file = await this.fileHelperService.store(uploadable.file, FileUploadDirectories.documents);

                const companyDocument = new CompanyDocument();
                companyDocument.fileId = file.id;
                companyDocument.name = uploadable.getUploadableName();

                if (this.isUploadingViewOnly) {
                    // View Only
                    companyDocument.isPublic = true;
                    companyDocument.type = DocumentType.viewOnly;
                } else {
                    // Acknowledgement
                    companyDocument.isPublic = false;
                    companyDocument.type = DocumentType.acknowledgement;
                }

                companyDocument.attach('documentTags', uploadable.tags);
                if (this.folderId) {
                    companyDocument.attach('parent_node_id', this.folderId);
                }

                await companyDocument.save();

                uploadable.status = 'complete';
            } catch (error) {
                uploadable.status = 'failed';
            }
        });

        await Promise.all(uploadPromises);

        const successfullyUploadedFiles = this.uploadables.filter((uploadable) => uploadable.status === 'complete');
        this.analyticService.trackEvent(AnalyticEvents.BulkUploaderViewOnlyDocumentSaved, {
            uploadedFileCount: successfullyUploadedFiles.length,
        });
    }

    /**
     * Checks if any other uploadable has the same name
     *
     * If so, sets the uploadable duplicatesLocalDocumentName value to true
     */
    private validateAgainstLocalNames(): void {
        this.uploadables = this.uploadables.map((uploadable) => {
            const nameIsDuplicatedLocally =
                Boolean(uploadable.getUploadableName()) &&
                this.uploadables.some(
                    (u) =>
                        u.getUploadableName().toLowerCase() === uploadable.getUploadableName().toLowerCase() &&
                        u !== uploadable
                );

            uploadable.duplicatesLocalDocumentName = nameIsDuplicatedLocally;

            return uploadable;
        });
    }

    /**
     * Checks if any persisted company document has the same name
     *
     * If so, sets the uploadable duplicatesPersistedDocumentName value to true
     */
    private async validateAgainstPersistedNames(): Promise<void> {
        const namesNotInMap = this.uploadables
            .map((u) => u.getUploadableName())
            .map((name) => name.toLowerCase())
            .filter((n) => n && !this.availableNames.has(n));

        await this.addNamesToMap(namesNotInMap);

        this.uploadables = this.uploadables.map((uploadable) => {
            const nameIsAvailable =
                !uploadable.getUploadableName() ||
                Boolean(this.availableNames.get(uploadable.getUploadableName().toLowerCase()));

            uploadable.duplicatesPersistedDocumentName = !nameIsAvailable;

            return uploadable;
        });
    }

    /**
     * Queries to see if the name if available and then stores
     * the result in the map
     */
    private async addNamesToMap(names: string[]): Promise<void> {
        for (const name of names) {
            const available = await this.documentNameAvailableService.checkIfDocumentNameIsExists(name, this.folderId);

            this.availableNames.set(name, available);
        }
    }

    private async loadTags(): Promise<void> {
        try {
            const [tags] = await DocumentTag.all();
            this.companyDocumentTags = tags;
        } catch (error) {
            this.notifyService.error(error);
        }
    }

    /**
     * Persists tags that are not yet persisted.
     *
     * If a non-persisted tag exists more than once,
     * the tag is only persisted once and the remainder are replaced
     */
    private async persistNotPersistedTags(): Promise<void> {
        const notPersistedTags = this.uploadables.reduce(
            (acc: DocumentTag[], cur: CompanyDocumentUploadable): DocumentTag[] => {
                const newTags = cur.tags.filter((tag) => !tag.isPersisted);
                return [...acc, ...newTags];
            },
            []
        );

        const uniqueNotPersistedTags = notPersistedTags.filter((tag, _, arr) => {
            const matchingTag = arr.find((t) => t.name === tag.name);

            return matchingTag === tag;
        });

        await Promise.all(uniqueNotPersistedTags.map((tag) => tag.save()));

        // they have been saved now
        // so we give the array a better name
        const newlyPersistedTags = uniqueNotPersistedTags;

        // update any duplicate new tags to use the newly persisted tag
        this.uploadables.forEach((u) => {
            u.tags = u.tags
                .map((tag) => {
                    if (tag.isPersisted) {
                        return tag;
                    }

                    return newlyPersistedTags.find((persistedTag) => persistedTag.name === tag.name);
                })
                .filter((tag): tag is DocumentTag => tag !== undefined);
        });
    }
}
